import QueryString from 'qs'
import {useCallback, useEffect, useState} from 'react'
import {ID, KTSVG, useDebounce} from '../../../../_metronic/helpers'
import {User} from '../../../_apiModels/_authenticationModels'
import {getUsers} from '../../../modules/user-management/users-list/core/_requests'
import {AncientButton} from './ancient-button'
import {AncientButtonSubmit} from './ancient-button-submit'
import {AncientCard} from './ancient-card'
import {AncientModal} from './ancient-modal'
import {AncientTable} from './ancient-table'
import {TBody} from './ancient-table-tbody'
import {TD} from './ancient-table-td'
import {TH} from './ancient-table-th'
import {THead} from './ancient-table-thead'
import {TR} from './ancient-table-tr'

interface AncientUserLookupProps {
  isOpen: boolean
  isSubmitting: boolean
  onClose: (val: boolean) => void
  onSelectUser: (userId: ID) => void
  onSubmittingChange: (val: boolean) => void
}

// unlike the UsersListSearchComponent this component is not tied to any context providers
export const AncientUserLookup = ({
  isOpen,
  isSubmitting,
  onClose,
  onSelectUser,
  onSubmittingChange,
}: AncientUserLookupProps) => {
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [selectedUserId, setSelectedUserId] = useState<ID>()
  const [users, setUsers] = useState<User[]>([])

  const debouncedSearchTerm = useDebounce(searchTerm, 200)

  useEffect(
    () => {
      const fetchUsers = async (query: string) => {
        try {
          const searchQueryString = QueryString.stringify({search: query})
          const res = await getUsers(searchQueryString)
          setUsers(res.data as User[])
        } catch (e) {
          console.log(e)
        }
      }

      if (debouncedSearchTerm === '') {
        setUsers([])
      }

      if (debouncedSearchTerm && searchTerm) {
        fetchUsers(debouncedSearchTerm)
      }
    },
    [debouncedSearchTerm] // Only call effect if debounced search term changes
  )

  const handleUserSelect = (id: ID) => {
    setSelectedUserId((prev) => (id !== prev ? id : undefined))
  }

  const resetValues = () => {
    setSelectedUserId(undefined)
    setSearchTerm('')
    setUsers([])
  }

  const handleClose = () => {
    resetValues()
    onClose(false)
  }

  const handleSubmit = useCallback(() => {
    onSubmittingChange(true)
    onSelectUser(selectedUserId)
    resetValues()
  }, [selectedUserId, onSubmittingChange])

  return (
    <AncientModal
      id='user_report_copy'
      header='Copy Report to User'
      isOpen={isOpen}
      onClose={handleClose}
      modalBodyClassName={'modalBodyOverride pt-0'}
      height={'90dvh'}
      maxWidth={'800px'}
      footer={
        <div className='d-flex justify-content-end py-5 pe-9 border-top'>
          <AncientButton
            className='btn-sm bg-secondary'
            displayText='Cancel'
            onClick={handleClose}
          />
          <AncientButtonSubmit
            buttonActionType='button'
            className='btn-sm bg-primary text-white me-0'
            displayText='Select User'
            onClick={handleSubmit}
            disabled={isSubmitting || !selectedUserId}
            submitting={isSubmitting}
          />
        </div>
      }
    >
      <div className='card-title border-bottom pb-8 mx-n7 px-7'>
        <div className='d-flex align-items-center position-relative my-1'>
          <KTSVG
            path='/media/icons/duotune/general/gen021.svg'
            className='svg-icon-1 position-absolute ms-6'
          />
          <input
            type='text'
            data-kt-user-table-filter='search'
            className='form-control form-control-solid w-250px ps-14'
            placeholder='Search users'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>
      <AncientCard>
        {users.length > 0 ? (
          <div>
            <AncientTable id='userTable'>
              <THead>
                <TR contentType='head'>
                  <TH>User Name</TH>
                  <TH>First Name</TH>
                  <TH>Last Name</TH>
                </TR>
              </THead>
              <TBody>
                {users.map((user: User) => {
                  const {email, firstName, lastName, id} = user
                  return (
                    <tr onClick={() => handleUserSelect(id)} key={id} tabIndex={0}>
                      <TD className='d-flex'>
                        <div className='form-check form-check-sm form-check-custom form-check-solid me-4'>
                          <input
                            className='form-check-input'
                            type='checkbox'
                            checked={selectedUserId === id}
                            aria-checked={selectedUserId === id}
                            aria-label={`Select user ${firstName} ${lastName}`}
                            readOnly
                            name={`${user.email} field`}
                          />
                        </div>
                        {email}
                      </TD>
                      <TD>{firstName}</TD>
                      <TD>{lastName}</TD>
                    </tr>
                  )
                })}
              </TBody>
            </AncientTable>
          </div>
        ) : (
          <p className='mt-10 text-center border p-8 mx-auto w-auto rounded'>
            Search for a user to view results
          </p>
        )}
      </AncientCard>
    </AncientModal>
  )
}
